import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSimulation, CLINICAL_ROLES } from '../context/SimulationContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import VitalMonitor from '../components/VitalMonitor';
import ActivityLogPanel, { calculateTimingStatus } from '../components/ActivityLogPanel';
import TeamNotificationsPanel from '../components/TeamNotificationsPanel';
import { DeteriorationAlertsPanel, DeathScreen, RecoveryAlert } from '../components/DeteriorationAlert';
import EventAlertsPanel from '../components/EventAlertsPanel';
import InstructorPanel from '../components/InstructorPanel';
import ContextualTour from '../components/ContextualTour';
import ProtocolStepsPanel, { ProtocolBlockedDialog } from '../components/ProtocolStepsPanel';
import { DiagnosisModal, TreatmentPlanModal } from '../components/simulation';
import { useStudentSimulationWebSocket, SupervisionIndicator } from '../components/SupervisionIndicator';
import { SupervisionSessionWidget } from '../components/SupervisionSessionWidget';
import { useVitalsTransition } from '../hooks/useVitalsTransition';
import { scenariosAPI, protocolsAPI, simulationsAPI } from '../api';
import { 
  Play,
  Stop,
  Pill,
  FirstAid,
  Clock,
  Warning,
  Info,
  CheckCircle,
  Spinner,
  ChatCircle,
  Stethoscope,
  Flask,
  Image,
  ArrowRight,
  PaperPlaneTilt,
  User as UserIcon,
  UserCircle,
  Shield,
  ListChecks,
  Lock,
  LockOpen,
  CheckSquare,
  Square,
  ProhibitInset,
  FileText,
  ClipboardText,
  Pause,
  Cube
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const EXAM_TYPES = [
  { id: 'auscultacion_cardiaca', label: 'Auscultación Cardíaca', icon: '❤️' },
  { id: 'auscultacion_pulmonar', label: 'Auscultación Pulmonar', icon: '🫁' },
  { id: 'palpacion_abdominal', label: 'Palpación Abdominal', icon: '🩺' },
  { id: 'examen_neurologico', label: 'Examen Neurológico', icon: '🧠' },
  { id: 'inspeccion_general', label: 'Inspección General', icon: '👁️' },
  { id: 'examen_extremidades', label: 'Extremidades', icon: '🦵' },
  { id: 'examen_piel', label: 'Examen de Piel', icon: '🩹' },
];

export default function SimulationPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    session,
    scenario,
    vitals,
    patientState,
    elapsedTime,
    medications: administeredMeds,
    actions: performedActions,
    medicationsCatalog,
    actionsCatalog,
    labTestsCatalog,
    imagingCatalog,
    isRunning,
    simulationPhase,
    setSimulationPhase,
    anamnesisHistory,
    physicalExamResults,
    labResults,
    imagingResults,
    startSimulation,
    beginSimulation,
    startTreatmentPhase,
    askPatient,
    performPhysicalExam,
    orderLabTests,
    orderImaging,
    administerMedication,
    performAction,
    endSimulation,
    formatTime,
    clinicalRole,
    timeline,
    teamNotifications,
    // Scenario-specific medications and actions
    scenarioMedications,
    scenarioActions,
    useScenarioFiltering,
    refreshAvailableItems,
    // Deterioration and death
    isDeceased,
    causeOfDeath,
    deteriorationAlerts,
    timeInParo,
    timeInCritical,
    // Recovery
    recoveryAlert,
    isRecovering,
    // Timeout de servidor (Fase C)
    isTimedOut,
    instructorPaused,
    // Setters for external updates (instructor panel)
    setVitals,
    setPatientState,
    // v10.3.1 Fase 2: transición progresiva de vitales
    activeVitalsTransition,
    setActiveVitalsTransition
  } = useSimulation();

  // Auth context for instructor check
  const { user } = useAuth();
  const isInstructor = user?.role === 'instructor' || user?.role === 'admin' || user?.role === 'super_admin';
  const isStudent = user?.role === 'alumno';

  // WebSocket for real-time supervision (only for students)
  const {
    isConnected: wsConnected,
    supervisors,
    hints,
    isBeingControlled,
    controllerName,
    sendVitalsUpdate,
    sendActionPerformed,
    requestControl,
    markHintAsRead,
    clearHints
  } = useStudentSimulationWebSocket(session?.id, isStudent && isRunning);

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [activityPanelExpanded, setActivityPanelExpanded] = useState(false);
  const [localDeteriorationAlerts, setLocalDeteriorationAlerts] = useState([]);
  const [showInstructorPanel, setShowInstructorPanel] = useState(true);

  // v10.3.1 Fase 2: interpolación local para transiciones progresivas
  // enviadas por el instructor. Auto-limpia `activeVitalsTransition` al
  // terminar la animación.
  const displayedVitals = useVitalsTransition(vitals, activeVitalsTransition);
  useEffect(() => {
    if (!activeVitalsTransition) return undefined;
    const dur = activeVitalsTransition.duration_ms || 5000;
    const startedAt = activeVitalsTransition.appliedAtMs || performance.now();
    const remaining = Math.max(0, dur - (performance.now() - startedAt));
    const t = setTimeout(() => setActiveVitalsTransition(null), remaining + 100);
    return () => clearTimeout(t);
  }, [activeVitalsTransition, setActiveVitalsTransition]);
  
  // Medication modal
  const [showMedicationModal, setShowMedicationModal] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [medicationDose, setMedicationDose] = useState('');
  const [medicationUnit, setMedicationUnit] = useState('');
  const [medicationRoute, setMedicationRoute] = useState('');
  const [selectedPresentacion, setSelectedPresentacion] = useState('');
  const [doseOptions, setDoseOptions] = useState([]);
  const [selectedDoseType, setSelectedDoseType] = useState('');
  const [loadingDoseOptions, setLoadingDoseOptions] = useState(false);
  
  // Anamnesis
  const [anamnesisQuestion, setAnamnesisQuestion] = useState('');
  const [askingPatient, setAskingPatient] = useState(false);
  
  // Physical Exam
  const [performingExam, setPerformingExam] = useState(false);
  
  // Lab tests
  const [selectedLabTests, setSelectedLabTests] = useState([]);
  const [orderingLabs, setOrderingLabs] = useState(false);
  
  // Imaging
  const [selectedImaging, setSelectedImaging] = useState([]);
  const [orderingImaging, setOrderingImaging] = useState(false);

  // Role Selection Modal (Fase 2)
  const [showRoleSelectionModal, setShowRoleSelectionModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState('medico');
  const [selectedProtocolId, setSelectedProtocolId] = useState('');
  const [availableProtocolsLocal, setAvailableProtocolsLocal] = useState([]);
  const [scenarioPreview, setScenarioPreview] = useState(null);
  const [loadingProtocols, setLoadingProtocols] = useState(false);

  // Estado del Protocolo Estricto
  const [protocolStatus, setProtocolStatus] = useState(null);
  const [showProtocolPanel, setShowProtocolPanel] = useState(false);
  const [protocolBlockedDialog, setProtocolBlockedDialog] = useState(null);

  // Diagnóstico y Plan de Tratamiento
  const [showDiagnosisModal, setShowDiagnosisModal] = useState(false);
  const [showTreatmentPlanModal, setShowTreatmentPlanModal] = useState(false);
  const [emittedDiagnoses, setEmittedDiagnoses] = useState([]);
  const [treatmentPlan, setTreatmentPlan] = useState(null);
  const [clinicalPermissions, setClinicalPermissions] = useState(null);

  // Cargar permisos clínicos
  const loadClinicalPermissions = useCallback(async () => {
    if (!session?.id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/simulations/${session.id}/clinical-permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setClinicalPermissions(data.permissions);
    } catch (error) {
      console.error('Error loading clinical permissions:', error);
    }
  }, [session?.id]);

  // Cargar diagnósticos emitidos
  const loadEmittedDiagnoses = useCallback(async () => {
    if (!session?.id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/simulations/${session.id}/diagnoses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setEmittedDiagnoses(data || []);
    } catch (error) {
      console.error('Error loading diagnoses:', error);
    }
  }, [session?.id]);

  // Cargar plan de tratamiento
  const loadTreatmentPlan = useCallback(async () => {
    if (!session?.id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/simulations/${session.id}/treatment-plan`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setTreatmentPlan(data);
    } catch (error) {
      console.error('Error loading treatment plan:', error);
    }
  }, [session?.id]);

  // Cargar datos clínicos cuando inicia la simulación (cualquier fase activa)
  useEffect(() => {
    if (session?.id && (simulationPhase === 'tratamiento' || simulationPhase === 'anamnesis' || simulationPhase === 'active')) {
      loadClinicalPermissions();
      loadEmittedDiagnoses();
      loadTreatmentPlan();
    }
  }, [session?.id, simulationPhase, loadClinicalPermissions, loadEmittedDiagnoses, loadTreatmentPlan]);

  // Función para cargar estado del protocolo
  const loadProtocolStatus = useCallback(async () => {
    if (!session?.id) return;
    try {
      const response = await simulationsAPI.getProtocolStatus(session.id);
      setProtocolStatus(response.data);
      // Mostrar panel si hay protocolo estricto
      if (response.data.has_protocol && response.data.modo_ejecucion === 'estricto') {
        setShowProtocolPanel(true);
      }
    } catch (error) {
      console.error('Error loading protocol status:', error);
    }
  }, [session?.id]);

  // Cargar estado del protocolo cuando inicia la simulación
  useEffect(() => {
    if (session?.id && simulationPhase === 'tratamiento') {
      loadProtocolStatus();
    }
  }, [session?.id, simulationPhase, loadProtocolStatus]);

  // Calculate timing status for all activities
  const allActivitiesWithTiming = useMemo(() => {
    const activities = [...administeredMeds, ...performedActions].map(activity => {
      const expectedWithin = activity.expected_within_seconds || 60;
      const criticalWithin = activity.critical_within_seconds || 180;
      const timingStatus = calculateTimingStatus(activity.tiempo || 0, expectedWithin, criticalWithin);
      return {
        ...activity,
        id: activity.id || `${activity.dosis ? 'med' : 'act'}-${activity.tiempo}-${activity.nombre}`,
        timing_status: timingStatus,
        role: activity.role || clinicalRole || session?.clinical_role || 'medico',
        elapsed_seconds: activity.tiempo,
        vitals_snapshot: activity.vitals_snapshot || null
      };
    });
    return activities;
  }, [administeredMeds, performedActions, clinicalRole, session?.clinical_role]);

  // Sincronizar alertas de deterioro desde el contexto
  useEffect(() => {
    if (deteriorationAlerts && deteriorationAlerts.length > 0) {
      setLocalDeteriorationAlerts(deteriorationAlerts);
    }
  }, [deteriorationAlerts]);

  // Enviar actualizaciones de signos vitales por WebSocket
  useEffect(() => {
    if (isStudent && wsConnected && vitals && isRunning) {
      sendVitalsUpdate(vitals, patientState);
    }
  }, [isStudent, wsConnected, vitals, patientState, isRunning, sendVitalsUpdate]);

  // Mostrar toast cuando hay alertas críticas
  useEffect(() => {
    if (timeInParo > 60 && timeInParo % 30 === 0) {
      toast.error(`PARO CARDÍACO - ${timeInParo}s sin respuesta`, {
        duration: 5000,
        icon: '💀'
      });
    } else if (timeInCritical > 120 && timeInCritical % 60 === 0) {
      toast.warning(`Estado CRÍTICO prolongado - ${timeInCritical}s`, {
        duration: 4000,
        icon: '⚠️'
      });
    }
  }, [timeInParo, timeInCritical]);

  // Manejar dismissal de alertas
  const handleDismissAlert = (index) => {
    setLocalDeteriorationAlerts(prev => prev.filter((_, i) => i !== index));
  };

  // Manejar pantalla de muerte
  const handleViewDebriefing = () => {
    // Navegar a debriefing
    endSimulation();
    navigate(`/simulations/${session?.id}/debriefing`);
  };

  const handleEndSimulationFromDeath = () => {
    endSimulation();
    navigate('/app/scenarios');
  };

  // Callback para actualizar vitales desde el panel del instructor
  const handleInstructorVitalsUpdate = (newVitals, newState) => {
    if (newVitals && setVitals) {
      setVitals(newVitals);
    }
    if (newState && setPatientState) {
      setPatientState(newState);
    }
  };

  // Load scenario preview and available protocols for role selection
  useEffect(() => {
    const loadScenarioPreview = async () => {
      if (!id) return;
      
      try {
        setLoadingProtocols(true);
        // Get scenario data
        const scenarioRes = await scenariosAPI.get(id);
        const scenarioData = scenarioRes.data;
        setScenarioPreview(scenarioData);
        
        // Load available protocols for this scenario
        if (scenarioData?.protocol_ids?.length > 0) {
          const protocolsRes = await protocolsAPI.getAll({ escenario_id: id });
          setAvailableProtocolsLocal(protocolsRes.data);
          // Set default protocol
          if (scenarioData.default_protocol_id) {
            setSelectedProtocolId(scenarioData.default_protocol_id);
          } else if (protocolsRes.data.length > 0) {
            setSelectedProtocolId(protocolsRes.data[0].id);
          }
        }
        
        // Always show role selection modal when entering simulation page
        // unless we're already in an active simulation (isRunning)
        if (!isRunning) {
          setShowRoleSelectionModal(true);
          setLoading(false);  // Allow modal to show while we wait for user selection
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error loading scenario preview:', error);
        toast.error('Error al cargar el escenario');
        navigate('/app/scenarios');
      } finally {
        setLoadingProtocols(false);
      }
    };
    
    loadScenarioPreview();
  }, [id, isRunning, navigate]);

  // Handle role selection and start simulation
  const handleConfirmRoleSelection = async () => {
    try {
      setLoading(true);
      // Pass options object with clinicalRole and protocolId
      await startSimulation(id, {
        clinicalRole: selectedRole,
        protocolId: selectedProtocolId || null
      });
      setShowRoleSelectionModal(false);
      toast.success(`Simulación iniciada como ${CLINICAL_ROLES[selectedRole]?.name || selectedRole}`);
      setLoading(false);
    } catch (error) {
      console.error('Error starting simulation:', error);
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Error al iniciar la simulación');
      if (error.response?.status === 403) {
        setShowRoleSelectionModal(false);
        navigate('/app/scenarios');
      }
      setLoading(false);
    }
  };

  const handleBeginSimulation = async () => {
    try {
      await beginSimulation();
      toast.success('Simulación iniciada - Fase de Anamnesis');
    } catch (error) {
      toast.error('Error al iniciar la simulación');
    }
  };

  const handleStartTreatment = async () => {
    try {
      await startTreatmentPhase();
      toast.success('Iniciando fase de tratamiento');
    } catch (error) {
      toast.error('Error al iniciar tratamiento');
    }
  };

  const handleEndSimulation = async () => {
    try {
      await endSimulation();
      navigate(`/debriefing/${session.id}`);
    } catch (error) {
      toast.error('Error al finalizar la simulación');
    }
  };

  // Anamnesis handlers
  const handleAskPatient = async () => {
    if (!anamnesisQuestion.trim()) return;
    
    setAskingPatient(true);
    try {
      await askPatient(anamnesisQuestion);
      setAnamnesisQuestion('');
    } catch (error) {
      toast.error('Error al comunicarse con el paciente');
    } finally {
      setAskingPatient(false);
    }
  };

  // Physical exam handlers
  const handlePhysicalExam = async (examType) => {
    setPerformingExam(examType);
    try {
      const result = await performPhysicalExam(examType);
      toast.success(`Examen realizado: ${result.hallazgo}`);
    } catch (error) {
      toast.error('Error al realizar examen');
    } finally {
      setPerformingExam(false);
    }
  };

  // Lab test handlers
  const handleToggleLabTest = (testId) => {
    setSelectedLabTests(prev => 
      prev.includes(testId) 
        ? prev.filter(t => t !== testId)
        : [...prev, testId]
    );
  };

  const handleOrderLabTests = async () => {
    if (selectedLabTests.length === 0) return;
    
    setOrderingLabs(true);
    try {
      await orderLabTests(selectedLabTests);
      toast.success(`${selectedLabTests.length} exámenes solicitados`);
      setSelectedLabTests([]);
    } catch (error) {
      toast.error('Error al solicitar exámenes');
    } finally {
      setOrderingLabs(false);
    }
  };

  // Imaging handlers
  const handleToggleImaging = (studyId) => {
    setSelectedImaging(prev => 
      prev.includes(studyId) 
        ? prev.filter(s => s !== studyId)
        : [...prev, studyId]
    );
  };

  const handleOrderImaging = async () => {
    if (selectedImaging.length === 0) return;
    
    setOrderingImaging(true);
    try {
      await orderImaging(selectedImaging);
      toast.success(`${selectedImaging.length} estudios solicitados`);
      setSelectedImaging([]);
    } catch (error) {
      toast.error('Error al solicitar imágenes');
    } finally {
      setOrderingImaging(false);
    }
  };

  // Medication handlers
  const openMedicationModal = async (med) => {
    setSelectedMedication(med);
    setSelectedPresentacion(med.presentaciones?.length ? med.presentaciones[0].codigo : '');
    setMedicationDose('');
    setSelectedDoseType('');
    setDoseOptions([]);
    
    // Handle both global catalog (unidades) and scenario-specific (unidad) formats
    const units = med.unidades || (med.unidad ? [med.unidad] : ['mg']);
    const routes = med.vias_permitidas || (med.via ? [med.via] : ['IV']);
    setMedicationUnit(units[0] || 'mg');
    setMedicationRoute(routes[0] || 'IV');
    setShowMedicationModal(true);
    
    // Cargar opciones de dosis controladas
    // Usar medication_id (que apunta al catálogo) si existe, sino usar id
    const medId = med.medication_id || med.id;
    if (session?.id && medId) {
      setLoadingDoseOptions(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/simulations/${session.id}/medication/${medId}/dose-options`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
        if (response.ok) {
          const data = await response.json();
          setDoseOptions(data.opciones || []);
          setMedicationUnit(data.unidad_dosis || units[0] || 'mg');
          
          // Si hay opciones y no permite dosis libre, preseleccionar la primera
          if (data.opciones && data.opciones.length > 0 && !data.permite_dosis_libre) {
            setSelectedDoseType(data.opciones[0].tipo);
            setMedicationDose(data.opciones[0].valor.toString());
          }
        }
      } catch (error) {
        console.error('Error loading dose options:', error);
        // Fallback: usar dosis del medicamento si existen
        if (med.dosis_minima && med.dosis_maxima) {
          const opts = [];
          if (med.dosis_minima === med.dosis_maxima) {
            opts.push({ tipo: 'unica', valor: med.dosis_minima, label: `${med.dosis_minima} ${med.unidad_dosis || 'mg'}` });
          } else {
            opts.push({ tipo: 'minima', valor: med.dosis_minima, label: `Dosis mínima: ${med.dosis_minima} ${med.unidad_dosis || 'mg'}` });
            opts.push({ tipo: 'maxima', valor: med.dosis_maxima, label: `Dosis máxima: ${med.dosis_maxima} ${med.unidad_dosis || 'mg'}` });
          }
          setDoseOptions(opts);
        }
      } finally {
        setLoadingDoseOptions(false);
      }
    }
  };

  const handleAdministerMedication = async () => {
    if (!selectedMedication || !medicationDose) return;

    try {
      const presentacion = (selectedMedication.presentaciones || []).find(p => p.codigo === selectedPresentacion);
      const result = await administerMedication({
        medication_id: selectedMedication.id || selectedMedication.medication_id,
        nombre: selectedMedication.nombre,
        dosis: parseFloat(medicationDose),
        unidad: medicationUnit,
        via: medicationRoute,
        tipo_dosis: selectedDoseType || 'custom',
        presentacion_codigo: presentacion?.codigo || null,
        presentacion_nombre: presentacion?.nombre || null,
        presentacion_forma: presentacion?.forma_farmaceutica || null
      });
      
      // Verificar si fue bloqueado por protocolo
      if (result?.blocked) {
        setProtocolBlockedDialog({
          message: result.message,
          pasoEsperado: result.paso_esperado,
          severidad: result.severidad
        });
        // Actualizar estado del protocolo
        loadProtocolStatus();
        return;
      }
      
      toast.success(`${selectedMedication.nombre} administrado (${medicationDose} ${medicationUnit})`);
      setShowMedicationModal(false);
      // Actualizar estado del protocolo después de éxito
      loadProtocolStatus();
    } catch (error) {
      toast.error('Error al administrar medicamento');
    }
  };

  const handlePerformAction = async (action) => {
    setActionLoading(action.id);
    try {
      const result = await performAction({
        action_id: action.id,
        nombre: action.nombre
      });
      
      // Verificar si fue bloqueado por protocolo
      if (result?.blocked) {
        setProtocolBlockedDialog({
          message: result.message,
          pasoEsperado: result.paso_esperado,
          severidad: result.severidad
        });
        // Actualizar estado del protocolo
        loadProtocolStatus();
        return;
      }
      
      toast.success(`${action.nombre} realizado`);
      // Actualizar estado del protocolo después de éxito
      loadProtocolStatus();
    } catch (error) {
      toast.error('Error al realizar acción');
    } finally {
      setActionLoading(null);
    }
  };

  const getPatientStateBadge = () => {
    const styles = {
      estable: 'bg-green-500 text-white',
      alerta: 'bg-yellow-500 text-white',
      critico: 'bg-red-500 text-white animate-pulse',
      paro: 'bg-red-700 text-white animate-pulse'
    };
    const labels = {
      estable: 'Estable',
      alerta: 'Alerta',
      critico: 'Crítico',
      paro: 'Paro Cardíaco'
    };
    return (
      <Badge className={styles[patientState] || styles.estable} data-testid="patient-state-badge">
        {labels[patientState] || patientState}
      </Badge>
    );
  };

  const getPhaseLabel = () => {
    const labels = {
      briefing: 'Briefing',
      anamnesis: 'Anamnesis',
      examen_fisico: 'Examen Físico',
      examenes: 'Exámenes',
      tratamiento: 'Tratamiento',
      en_curso: 'En Curso'
    };
    return labels[simulationPhase] || simulationPhase;
  };

  // Group actions and medications by category
  // Use scenario-specific items if available, otherwise fall back to global catalog
  const effectiveMedications = scenarioMedications?.length > 0 ? scenarioMedications : medicationsCatalog;
  const effectiveActions = scenarioActions?.length > 0 ? scenarioActions : actionsCatalog;

  const groupedActions = effectiveActions.reduce((acc, action) => {
    const cat = action.categoria;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(action);
    return acc;
  }, {});

  const groupedMedications = effectiveMedications.reduce((acc, med) => {
    const cat = med.categoria;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(med);
    return acc;
  }, {});

  if (loading && !showRoleSelectionModal) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Spinner size={48} className="animate-spin text-blue-500" />
      </div>
    );
  }

  // Show Role Selection Modal even if still loading session data
  if (showRoleSelectionModal) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        {/* Role Selection Modal */}
        <Dialog open={showRoleSelectionModal} onOpenChange={(open) => {
          // Prevent closing with escape or backdrop click - user must choose
          if (!open && !session) return;
          setShowRoleSelectionModal(open);
        }}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <UserCircle size={28} className="text-blue-400" />
                Selecciona tu Rol Clínico
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Antes de iniciar la simulación, selecciona el rol que asumirás en el equipo de salud y el protocolo a seguir.
              </DialogDescription>
            </DialogHeader>
            
            {scenarioPreview && (
              <div className="bg-slate-900/50 rounded-lg p-3 mb-4 border border-slate-700">
                <h4 className="font-medium text-white mb-1">{scenarioPreview.nombre}</h4>
                <p className="text-sm text-slate-400">{scenarioPreview.especialidad} - Nivel: {scenarioPreview.nivel}</p>
              </div>
            )}
            
            <div className="space-y-6">
              {/* Role Selection */}
              <div>
                <Label className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                  <Shield size={16} />
                  Rol en el Equipo de Salud
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                  {/* Solo Médico y Enfermera disponibles para simulación */}
                  {Object.entries(CLINICAL_ROLES).filter(([roleId]) => ['medico', 'enfermera'].includes(roleId)).map(([roleId, role]) => (
                    <div
                      key={roleId}
                      onClick={() => setSelectedRole(roleId)}
                      className={`cursor-pointer rounded-lg border-2 p-3 transition-all relative ${
                        selectedRole === roleId
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-slate-600 bg-slate-700/50 hover:border-slate-500'
                      }`}
                      data-testid={`role-option-${roleId}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          selectedRole === roleId ? 'bg-blue-500' : 'bg-slate-600'
                        }`}>
                          {roleId === 'medico' && <Stethoscope size={20} className="text-white" />}
                          {roleId === 'enfermera' && <FirstAid size={20} className="text-white" />}
                          {roleId === 'tens' && <UserIcon size={20} className="text-white" />}
                          {roleId === 'paramedico' && <FirstAid size={20} className="text-white" />}
                        </div>
                        <div>
                          <p className="font-medium text-white">{role.name}</p>
                          <p className="text-xs text-slate-400 line-clamp-2">{role.description}</p>
                        </div>
                      </div>
                      {selectedRole === roleId && (
                        <CheckCircle size={20} className="absolute top-2 right-2 text-blue-400" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Protocol Selection */}
              <div>
                <Label className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                  <ListChecks size={16} />
                  Protocolo Clínico
                </Label>
                <Select
                  value={selectedProtocolId}
                  onValueChange={setSelectedProtocolId}
                  disabled={loadingProtocols || availableProtocolsLocal.length === 0}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="protocol-select">
                    <SelectValue placeholder={loadingProtocols ? "Cargando protocolos..." : "Selecciona un protocolo"} />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {availableProtocolsLocal.map(protocol => (
                      <SelectItem key={protocol.id} value={protocol.id} className="text-white hover:bg-slate-600">
                        <div className="flex items-center gap-2">
                          <span>{protocol.nombre}</span>
                          <Badge variant="outline" className="text-xs">{protocol.tipo}</Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {availableProtocolsLocal.length === 0 && !loadingProtocols && (
                  <p className="text-xs text-slate-500 mt-1">
                    Este escenario no tiene protocolos específicos asignados
                  </p>
                )}
              </div>

              {/* Selected Role Info */}
              {selectedRole && CLINICAL_ROLES[selectedRole] && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                  <p className="text-sm text-blue-300">
                    <strong>Responsabilidades como {CLINICAL_ROLES[selectedRole].name}:</strong>
                  </p>
                  <p className="text-sm text-slate-300 mt-1">
                    {CLINICAL_ROLES[selectedRole].description}
                  </p>
                  <p className="text-xs text-slate-400 mt-2">
                    ⚠️ Las acciones fuera de tu rol o protocolo serán registradas como eventos adversos.
                  </p>
                </div>
              )}
            </div>

            <DialogFooter className="mt-4">
              <Button 
                variant="outline" 
                onClick={() => navigate('/app/scenarios')}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleConfirmRoleSelection}
                disabled={!selectedRole || loading}
                className="bg-blue-600 hover:bg-blue-700 gap-2"
                data-testid="confirm-role-btn"
              >
                {loading ? (
                  <>
                    <Spinner size={18} className="animate-spin" />
                    Iniciando...
                  </>
                ) : (
                  <>
                    <Play size={18} weight="fill" />
                    Iniciar Simulación
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Briefing Phase
  if (simulationPhase === 'briefing') {
    return (
      <div className="min-h-screen bg-slate-50 p-4 md:p-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="text-center">
            <Badge className="mb-4">Fase de Briefing</Badge>
            <h1 className="text-3xl font-bold font-['Chivo'] text-slate-900 mb-2" data-testid="briefing-title">
              {scenario?.nombre}
            </h1>
            <p className="text-slate-500 capitalize">
              {scenario?.especialidad?.replace('_', ' ')} - Fidelidad {scenario?.nivel}
            </p>
          </div>

          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-800">
                <Info size={20} />
                Contexto Clínico
              </CardTitle>
            </CardHeader>
            <CardContent className="text-blue-900">
              <p className="mb-4">{scenario?.briefing}</p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <strong>Paciente:</strong> {scenario?.sexo_paciente}, {scenario?.edad_paciente} años
                </div>
                <div>
                  <strong>Antecedentes:</strong> {scenario?.antecedentes}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Objetivos de Aprendizaje</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {scenario?.objetivos?.map((obj, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-green-500 mt-0.5" />
                    <span>{obj}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {scenario?.evaluacion?.errores_criticos?.length > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-800">
                  <Warning size={20} />
                  Errores Críticos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-red-700">
                  {scenario.evaluacion.errores_criticos.map((error, i) => (
                    <li key={i}>• {error}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-center gap-4">
            <Button variant="outline" onClick={() => navigate('/app/scenarios')} data-testid="cancel-btn">
              Cancelar
            </Button>
            <Button size="lg" onClick={handleBeginSimulation} className="gap-2" data-testid="begin-simulation-btn">
              <Play size={20} weight="fill" />
              Comenzar Simulación
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Active Simulation Phase
  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Fase 3 tour: mini-tour contextual al entrar a una simulación real por primera vez */}
      {user?.role === 'alumno' && !session?.is_guided_demo && (
        <ContextualTour tourId="sim-first-time" ready={!!session?.id && !!scenario?.id} delay={1500} />
      )}
      {/* Fase D: banner de pausa por instructor */}
      {instructorPaused && (
        <div className="bg-amber-500 text-amber-950 px-4 py-2 text-center text-sm font-semibold flex items-center justify-center gap-2" data-testid="instructor-paused-banner">
          <Pause size={16} weight="fill" />
          Simulación en pausa por el instructor — el tiempo está detenido
        </div>
      )}
      {/* Fase C: modal bloqueante de tiempo agotado */}
      <Dialog open={isTimedOut && !!session} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" data-testid="timeout-modal" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Clock size={22} /> Tiempo agotado
            </DialogTitle>
            <DialogDescription>
              Se alcanzó el tiempo máximo del escenario. La simulación fue finalizada automáticamente
              por el servidor y se está generando tu evaluación (debriefing).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button className="w-full" onClick={() => navigate(`/debriefing/${session?.id}`)} data-testid="timeout-view-debriefing-btn">
              Ver Debriefing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Top Bar */}
      <div className="bg-slate-800 border-b border-slate-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-white font-semibold hidden md:block">{scenario?.nombre}</h1>
          {getPatientStateBadge()}
          <Badge variant="outline" className="text-slate-300 border-slate-500">
            {getPhaseLabel()}
          </Badge>
        </div>
        <div className="flex items-center gap-3">
          {/* v10.11.0 — Vincular móvil XR si el escenario tiene xr_config activo */}
          {scenario?.xr_config?.enabled && session?.id && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/app/xr/link?session=${session.id}`)}
              className="gap-1.5 bg-cyan-500/10 border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/20 hover:text-cyan-200"
              data-testid="link-xr-device-btn"
              title="Genera un QR para escanear con tu móvil/tablet y usar AR"
            >
              <Cube size={16} weight="fill" /> Vincular XR
            </Button>
          )}
          {/* v10.2.4: widget flotante de sesión de supervisión (alumno) */}
          {isStudent && (
            <SupervisionSessionWidget
              supervisors={supervisors}
              isBeingControlled={isBeingControlled}
              controllerName={controllerName}
              onRequestControl={requestControl}
              wsConnected={wsConnected}
            />
          )}
          <div className="flex items-center gap-2 text-white">
            <Clock size={20} />
            <span className="font-vital text-2xl" data-testid="timer">
              {formatTime(elapsedTime)}
            </span>
            <span className="text-slate-400 text-sm">
              / {formatTime(scenario?.tiempo_maximo || 600)}
            </span>
          </div>
          <Button 
            variant="destructive" 
            onClick={handleEndSimulation}
            className="gap-2"
            data-testid="end-simulation-btn"
          >
            <Stop size={18} weight="fill" />
            Finalizar
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-0 overflow-hidden">
        {/* Left: Vital Signs Monitor */}
        <div className="lg:col-span-7 p-4 flex flex-col gap-4 overflow-auto">
          <div className="min-h-[250px] h-[35vh]" data-tour="simulation-monitor">
            <VitalMonitor 
              vitals={displayedVitals} 
              patientState={patientState} 
              showPAM={scenario?.requires_pam || true}
              scenario={scenario}
            />
          </div>
          
          {/* Phase-specific interaction panel */}
          {(simulationPhase === 'anamnesis' || simulationPhase === 'examen_fisico' || simulationPhase === 'examenes') && (
            <Card className="bg-slate-800 border-slate-700">
              <Tabs defaultValue="anamnesis" className="w-full">
                <TabsList className="w-full grid grid-cols-4 bg-slate-700">
                  <TabsTrigger value="anamnesis" className="text-xs sm:text-sm data-[state=active]:bg-slate-600" data-tour="simulation-anamnesis">
                    <ChatCircle size={16} className="mr-1 hidden sm:inline" />
                    Anamnesis
                  </TabsTrigger>
                  <TabsTrigger value="examen" className="text-xs sm:text-sm data-[state=active]:bg-slate-600">
                    <Stethoscope size={16} className="mr-1 hidden sm:inline" />
                    Ex. Físico
                  </TabsTrigger>
                  <TabsTrigger value="labs" className="text-xs sm:text-sm data-[state=active]:bg-slate-600">
                    <Flask size={16} className="mr-1 hidden sm:inline" />
                    Laboratorio
                  </TabsTrigger>
                  <TabsTrigger value="imaging" className="text-xs sm:text-sm data-[state=active]:bg-slate-600">
                    <Image size={16} className="mr-1 hidden sm:inline" />
                    Imágenes
                  </TabsTrigger>
                </TabsList>

                {/* Anamnesis Tab */}
                <TabsContent value="anamnesis" className="p-4">
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        value={anamnesisQuestion}
                        onChange={(e) => setAnamnesisQuestion(e.target.value)}
                        placeholder="Pregunte al paciente..."
                        className="bg-slate-700 border-slate-600 text-white"
                        onKeyPress={(e) => e.key === 'Enter' && handleAskPatient()}
                        data-testid="anamnesis-input"
                      />
                      <Button onClick={handleAskPatient} disabled={askingPatient || !anamnesisQuestion.trim()} data-testid="ask-patient-btn">
                        {askingPatient ? <Spinner size={18} className="animate-spin" /> : <PaperPlaneTilt size={18} />}
                      </Button>
                    </div>
                    <ScrollArea className="h-40 border border-slate-700 rounded-lg p-3">
                      <div className="space-y-3">
                        {anamnesisHistory.map((item, i) => (
                          <div key={`anamnesis-${item.tiempo || i}-${item.pregunta?.slice(0,20)}`} className="space-y-1">
                            <div className="flex items-start gap-2">
                              <UserIcon size={16} className="text-blue-400 mt-1" />
                              <p className="text-sm text-blue-300">{item.pregunta}</p>
                            </div>
                            <div className="flex items-start gap-2 pl-6">
                              <p className="text-sm text-white bg-slate-700 rounded-lg p-2">{item.respuesta}</p>
                            </div>
                          </div>
                        ))}
                        {anamnesisHistory.length === 0 && (
                          <p className="text-slate-500 text-sm text-center">Realice preguntas al paciente</p>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </TabsContent>

                {/* Physical Exam Tab */}
                <TabsContent value="examen" className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {EXAM_TYPES.map((exam) => {
                      const done = physicalExamResults.find(r => r.tipo === exam.id);
                      return (
                        <Button
                          key={exam.id}
                          variant={done ? "secondary" : "outline"}
                          className={`h-auto py-3 flex flex-col gap-1 ${done ? 'bg-green-900/30 border-green-700' : 'bg-slate-700 border-slate-600'} text-white`}
                          onClick={() => handlePhysicalExam(exam.id)}
                          disabled={performingExam === exam.id}
                          data-testid={`exam-${exam.id}`}
                        >
                          {performingExam === exam.id ? (
                            <Spinner size={20} className="animate-spin" />
                          ) : (
                            <>
                              <span className="text-lg">{exam.icon}</span>
                              <span className="text-xs">{exam.label}</span>
                            </>
                          )}
                        </Button>
                      );
                    })}
                  </div>
                  {physicalExamResults.length > 0 && (
                    <ScrollArea className="h-32 mt-4 border border-slate-700 rounded-lg p-3">
                      <div className="space-y-2">
                        {physicalExamResults.map((result) => (
                          <div key={`exam-${result.tipo}-${result.tiempo || ''}`} className="text-sm">
                            <span className={`font-medium ${result.es_normal ? 'text-green-400' : 'text-yellow-400'}`}>
                              {EXAM_TYPES.find(e => e.id === result.tipo)?.label}:
                            </span>
                            <span className="text-white ml-2">{result.hallazgo}</span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </TabsContent>

                {/* Lab Tests Tab */}
                <TabsContent value="labs" className="p-4">
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {labTestsCatalog.slice(0, 8).map((test) => (
                      <div
                        key={test.id}
                        className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${
                          selectedLabTests.includes(test.nombre)
                            ? 'border-blue-500 bg-blue-900/30'
                            : 'border-slate-600 bg-slate-700'
                        }`}
                        onClick={() => handleToggleLabTest(test.nombre)}
                        data-testid={`lab-${test.id}`}
                      >
                        <Checkbox checked={selectedLabTests.includes(test.nombre)} />
                        <span className="text-sm text-white">{test.nombre}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={handleOrderLabTests}
                    disabled={selectedLabTests.length === 0 || orderingLabs}
                    className="w-full"
                    data-testid="order-labs-btn"
                  >
                    {orderingLabs ? <Spinner size={18} className="animate-spin mr-2" /> : null}
                    Solicitar Exámenes ({selectedLabTests.length})
                  </Button>
                  {labResults.length > 0 && (
                    <ScrollArea className="h-32 mt-4 border border-slate-700 rounded-lg p-3">
                      <div className="space-y-2">
                        {labResults.map((result) => (
                          <div key={`lab-${result.nombre}-${result.tiempo || ''}`} className="text-sm flex justify-between">
                            <span className="text-slate-300">{result.nombre}:</span>
                            <span className={result.es_normal ? 'text-green-400' : 'text-red-400'}>
                              {result.valor}
                            </span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </TabsContent>

                {/* Imaging Tab */}
                <TabsContent value="imaging" className="p-4">
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {imagingCatalog.slice(0, 6).map((study) => (
                      <div
                        key={study.id}
                        className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${
                          selectedImaging.includes(study.nombre)
                            ? 'border-purple-500 bg-purple-900/30'
                            : 'border-slate-600 bg-slate-700'
                        }`}
                        onClick={() => handleToggleImaging(study.nombre)}
                        data-testid={`imaging-${study.id}`}
                      >
                        <Checkbox checked={selectedImaging.includes(study.nombre)} />
                        <span className="text-sm text-white">{study.nombre}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={handleOrderImaging}
                    disabled={selectedImaging.length === 0 || orderingImaging}
                    className="w-full"
                    data-testid="order-imaging-btn"
                  >
                    {orderingImaging ? <Spinner size={18} className="animate-spin mr-2" /> : null}
                    Solicitar Estudios ({selectedImaging.length})
                  </Button>
                  {imagingResults.length > 0 && (
                    <ScrollArea className="h-32 mt-4 border border-slate-700 rounded-lg p-3">
                      <div className="space-y-2">
                        {imagingResults.map((result) => (
                          <div key={`img-${result.nombre}-${result.tiempo || ''}`} className="text-sm">
                            <span className="text-purple-400 font-medium">{result.nombre}:</span>
                            <span className="text-white ml-2">{result.hallazgo}</span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </TabsContent>
              </Tabs>
              
              {/* Phase transition button */}
              {simulationPhase !== 'tratamiento' && simulationPhase !== 'en_curso' && (
                <div className="p-4 border-t border-slate-700">
                  <Button 
                    onClick={handleStartTreatment} 
                    className="w-full gap-2"
                    data-testid="start-treatment-btn"
                  >
                    Iniciar Tratamiento
                    <ArrowRight size={18} />
                  </Button>
                </div>
              )}
            </Card>
          )}

          {/* Activity Log - New Expandable Panel - Always visible */}
          <div className="flex-shrink-0 mt-2">
            <ActivityLogPanel
              activities={allActivitiesWithTiming}
              timeline={timeline || []}
              teamNotifications={teamNotifications || []}
              clinicalRole={clinicalRole || session?.clinical_role || 'medico'}
              elapsedTime={elapsedTime}
              formatTime={formatTime}
              isExpanded={activityPanelExpanded}
              onToggleExpand={setActivityPanelExpanded}
            />
          </div>
        </div>

        {/* Right: Control Panel - Medications & Actions */}
        <div className="lg:col-span-5 bg-slate-800 border-l border-slate-700 flex flex-col overflow-hidden">
          <Tabs defaultValue="actions" className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="w-full rounded-none bg-slate-700 border-b border-slate-600 flex-shrink-0">
              <TabsTrigger value="actions" className="flex-1 data-[state=active]:bg-slate-600" data-tour="simulation-actions">
                <FirstAid size={18} className="mr-2" />
                Acciones
              </TabsTrigger>
              <TabsTrigger value="medications" className="flex-1 data-[state=active]:bg-slate-600" data-tour="simulation-medications">
                <Pill size={18} className="mr-2" />
                Fármacos
              </TabsTrigger>
            </TabsList>

            <TabsContent value="actions" className="flex-1 m-0 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="p-3 space-y-3">
                  {Object.entries(groupedActions).map(([category, actions]) => (
                    <div key={category}>
                      <h3 className="text-slate-400 text-xs font-medium mb-2 uppercase sticky top-0 bg-slate-800 py-1">
                        {category}
                      </h3>
                      <div className="grid grid-cols-2 xl:grid-cols-3 gap-1.5">
                        {actions.map((action) => (
                          <Button
                            key={action.id || action.action_id}
                            variant="outline"
                            className={`h-auto py-2 px-2 flex flex-col items-start text-left bg-slate-700 border-slate-600 text-white hover:bg-slate-600 ${
                              action.es_obligatorio ? 'ring-1 ring-red-500/50' : ''
                            } ${action.permite_error ? 'ring-1 ring-yellow-500/50' : ''} ${
                              action.bloqueado ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            onClick={() => handlePerformAction(action)}
                            disabled={actionLoading === (action.id || action.action_id) || action.bloqueado}
                            data-testid={`action-${action.id || action.action_id}`}
                          >
                            {actionLoading === (action.id || action.action_id) ? (
                              <Spinner size={14} className="animate-spin" />
                            ) : (
                              <>
                                <div className="flex items-center gap-1 w-full">
                                  <span className="text-xs font-medium line-clamp-2 flex-1">{action.nombre}</span>
                                  {action.es_obligatorio && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" title="Obligatorio" />
                                  )}
                                  {action.permite_error && (
                                    <Warning size={10} className="text-yellow-400 flex-shrink-0" />
                                  )}
                                </div>
                                {(action.duracion_segundos || action.tiempo_esperado_segundos) && (
                                  <span className="text-[10px] text-slate-400">
                                    {action.duracion_segundos || action.tiempo_esperado_segundos}s
                                  </span>
                                )}
                              </>
                            )}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="medications" className="flex-1 m-0 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="p-3 space-y-3">
                  {Object.entries(groupedMedications).map(([category, meds]) => (
                    <div key={category}>
                      <h3 className="text-slate-400 text-xs font-medium mb-2 uppercase sticky top-0 bg-slate-800 py-1">
                        {category}
                      </h3>
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-1.5">
                        {meds.map((med) => (
                          <Button
                            key={med.id || med.medication_id}
                            variant="outline"
                            className={`h-auto py-2 px-2 flex justify-between items-center bg-slate-700 border-slate-600 text-white hover:bg-slate-600 ${
                              med.es_obligatorio ? 'ring-1 ring-red-500/50' : ''
                            } ${med.permite_error ? 'ring-1 ring-yellow-500/50' : ''}`}
                            onClick={() => openMedicationModal(med)}
                            data-testid={`medication-${med.id || med.medication_id}`}
                          >
                            <div className="flex items-center gap-1 flex-1 min-w-0">
                              <span className="text-xs font-medium truncate">{med.nombre}</span>
                              {med.es_obligatorio && (
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" title="Obligatorio" />
                              )}
                              {med.permite_error && (
                                <Warning size={10} className="text-yellow-400 flex-shrink-0" />
                              )}
                            </div>
                            <span className="text-[10px] text-slate-400 ml-1 flex-shrink-0">
                              {med.dosis_sugerida}{med.unidad}
                            </span>
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
          
          {/* Diagnosis & Treatment Plan Section - Show for doctors in active simulation */}
          {session?.id && session?.clinical_role === 'medico' && simulationPhase !== 'briefing' && (
            <div className="p-3 border-t border-slate-700 bg-slate-800 space-y-2">
              <h4 className="text-xs font-medium text-slate-400 uppercase">Decisiones Clínicas</h4>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-blue-600/20 border-blue-500/50 text-blue-200 hover:bg-blue-600/30"
                  onClick={() => setShowDiagnosisModal(true)}
                  data-testid="emit-diagnosis-btn"
                >
                  <FileText size={16} className="mr-2" />
                  {emittedDiagnoses.length > 0 ? `Diagnóstico (${emittedDiagnoses.length})` : 'Emitir Diagnóstico'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-green-600/20 border-green-500/50 text-green-200 hover:bg-green-600/30"
                  onClick={() => setShowTreatmentPlanModal(true)}
                  data-testid="create-treatment-btn"
                >
                  <ClipboardText size={16} className="mr-2" />
                  {treatmentPlan ? 'Ver Plan' : 'Crear Plan'}
                </Button>
              </div>
              
              {/* Show emitted diagnoses summary */}
              {emittedDiagnoses.length > 0 && (
                <div className="mt-2 space-y-1">
                  {emittedDiagnoses.map((d, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-300 bg-slate-700/50 px-2 py-1 rounded">
                      <Badge variant="outline" className="text-[10px] px-1">{d.tipo}</Badge>
                      <span className="font-mono text-slate-400">{d.codigo_cie10}</span>
                      <span className="truncate">{d.nombre}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Show treatment plan summary */}
              {treatmentPlan && (
                <div className="mt-2 text-xs text-slate-300 bg-slate-700/50 px-2 py-1 rounded">
                  <span className="text-green-400">Plan creado:</span> {treatmentPlan.resumen?.medicamentos || 0} meds, {treatmentPlan.resumen?.indicaciones || 0} ind.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Medication Modal */}
      <Dialog open={showMedicationModal} onOpenChange={setShowMedicationModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Administrar {selectedMedication?.nombre}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Dosis sugerida: {selectedMedication?.dosis_sugerida}
            </p>
            
            {/* Sistema de Dosis Controladas */}
            {loadingDoseOptions ? (
              <div className="flex items-center justify-center py-4">
                <Spinner size={24} className="animate-spin text-blue-600" />
                <span className="ml-2 text-sm text-slate-500">Cargando opciones de dosis...</span>
              </div>
            ) : doseOptions.length > 0 ? (
              <div className="space-y-3">
                <Label className="text-base font-semibold">Seleccione dosis</Label>
                <div className="space-y-2">
                  {doseOptions.map((option) => (
                    <div
                      key={option.tipo}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedDoseType === option.tipo
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => {
                        setSelectedDoseType(option.tipo);
                        setMedicationDose(option.valor.toString());
                      }}
                      data-testid={`dose-option-${option.tipo}`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedDoseType === option.tipo ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                      }`}>
                        {selectedDoseType === option.tipo && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{option.label}</div>
                        {option.descripcion && (
                          <div className="text-xs text-slate-500">{option.descripcion}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  Solo puede seleccionar las dosis predefinidas. No se permite ingreso libre de valores.
                </p>
              </div>
            ) : (
              /* Modo legacy - input libre */
              <div className="space-y-2">
                <Label htmlFor="dose">Dosis</Label>
                <Input
                  id="dose"
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="Ingrese dosis"
                  value={medicationDose}
                  onChange={(e) => setMedicationDose(e.target.value)}
                  data-testid="medication-dose-input"
                />
              </div>
            )}

            {/* Presentación farmacéutica */}
            {selectedMedication?.presentaciones?.length > 0 && (
              <div className="space-y-2">
                <Label>Presentación farmacéutica</Label>
                <Select value={selectedPresentacion} onValueChange={setSelectedPresentacion}>
                  <SelectTrigger data-testid="medication-presentation-select">
                    <SelectValue placeholder="Seleccionar presentación" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedMedication.presentaciones.map((p) => (
                      <SelectItem key={p.codigo} value={p.codigo} data-testid={`presentation-option-${p.codigo}`}>
                        <div className="flex flex-col text-left">
                          <span>{p.nombre}</span>
                          <span className="text-xs text-slate-500">
                            {p.forma_farmaceutica}
                            {p.concentracion ? ` · ${p.concentracion} ${p.unidad_concentracion || ''}` : ''}
                            {p.requiere_dilucion ? ' · Requiere dilución' : ''}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(() => {
                  const p = selectedMedication.presentaciones.find(x => x.codigo === selectedPresentacion);
                  return p?.requiere_dilucion && p?.instrucciones_preparacion ? (
                    <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded" data-testid="presentation-dilution-note">
                      Preparación: {p.instrucciones_preparacion}
                    </p>
                  ) : null;
                })()}
              </div>
            )}

            <div className="space-y-2">
              <Label>Vía de administración</Label>
              <Select value={medicationRoute} onValueChange={setMedicationRoute}>
                <SelectTrigger data-testid="medication-route-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(selectedMedication?.vias_permitidas || (selectedMedication?.via ? [selectedMedication.via] : ['IV']))?.map((via) => (
                    <SelectItem key={via} value={via}>{via}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Mostrar dosis seleccionada */}
            {medicationDose && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-500">Dosis a administrar: </span>
                <span className="font-semibold">{medicationDose} {medicationUnit}</span>
                <span className="text-sm text-slate-500"> vía </span>
                <span className="font-semibold">{medicationRoute}</span>
                {selectedPresentacion && selectedMedication?.presentaciones?.length > 0 && (
                  <>
                    <span className="text-sm text-slate-500"> — </span>
                    <span className="font-semibold text-xs" data-testid="presentation-summary">
                      {selectedMedication.presentaciones.find(p => p.codigo === selectedPresentacion)?.nombre}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMedicationModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAdministerMedication}
              disabled={!medicationDose}
              data-testid="confirm-medication-btn"
            >
              Administrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Team Notifications - Floating Panel */}
      <TeamNotificationsPanel 
        notifications={teamNotifications || []}
      />

      {/* Deterioration Alerts - Floating Panel */}
      <DeteriorationAlertsPanel 
        alerts={localDeteriorationAlerts}
        onDismissAlert={handleDismissAlert}
      />

      {/* Event Alerts - Eventos condicionales del escenario (IAM Demo) */}
      <EventAlertsPanel />

      {/* Death Screen - Modal Overlay */}
      {isDeceased && (
        <DeathScreen
          causeOfDeath={causeOfDeath}
          elapsedTime={elapsedTime}
          onViewDebriefing={handleViewDebriefing}
          onEndSimulation={handleEndSimulationFromDeath}
        />
      )}

      {/* Recovery Alert - Floating notification */}
      {isRecovering && recoveryAlert && (
        <RecoveryAlert
          recoveryData={recoveryAlert}
          onDismiss={() => {}}
        />
      )}

      {/* Instructor Panel - Solo visible para instructores/admin */}
      {isInstructor && session?.id && (
        <InstructorPanel
          sessionId={session.id}
          currentVitals={vitals}
          patientState={patientState}
          elapsedTime={elapsedTime}
          onVitalsUpdate={handleInstructorVitalsUpdate}
          isVisible={showInstructorPanel}
          onToggleVisibility={() => setShowInstructorPanel(!showInstructorPanel)}
        />
      )}

      {/* Protocol Steps Panel - Floating Panel */}
      {protocolStatus?.has_protocol && protocolStatus?.has_strict_sequence && (
        <div className="fixed top-24 right-4 z-30 w-80">
          <ProtocolStepsPanel 
            protocolStatus={protocolStatus}
            collapsed={!showProtocolPanel}
          />
          <button
            onClick={() => setShowProtocolPanel(!showProtocolPanel)}
            className="mt-2 w-full text-center text-xs text-blue-600 hover:text-blue-800"
          >
            {showProtocolPanel ? 'Minimizar' : 'Expandir'} panel de protocolo
          </button>
        </div>
      )}

      {/* Protocol Blocked Dialog */}
      <ProtocolBlockedDialog
        isOpen={!!protocolBlockedDialog}
        onClose={() => setProtocolBlockedDialog(null)}
        message={protocolBlockedDialog?.message}
        pasoEsperado={protocolBlockedDialog?.pasoEsperado}
        severidad={protocolBlockedDialog?.severidad}
      />

      {/* Supervision Indicator for Students */}
      {isStudent && (
        <SupervisionIndicator
          supervisors={supervisors}
          hints={hints}
          isBeingControlled={isBeingControlled}
          controllerName={controllerName}
          onMarkHintRead={markHintAsRead}
          onClearHints={clearHints}
        />
      )}

      {/* Diagnosis Modal */}
      <DiagnosisModal
        isOpen={showDiagnosisModal}
        onClose={() => setShowDiagnosisModal(false)}
        sessionId={session?.id}
        emittedDiagnoses={emittedDiagnoses}
        onDiagnosisEmitted={(diagnosis) => {
          setEmittedDiagnoses([...emittedDiagnoses, diagnosis]);
        }}
      />

      {/* Treatment Plan Modal */}
      <TreatmentPlanModal
        isOpen={showTreatmentPlanModal}
        onClose={() => setShowTreatmentPlanModal(false)}
        sessionId={session?.id}
        emittedDiagnoses={emittedDiagnoses}
        existingPlan={treatmentPlan}
        onPlanCreated={(plan) => {
          setTreatmentPlan(plan);
        }}
      />
    </div>
  );
}
